import { useEffect, useMemo, useState } from 'react';
import { useAppSelector, useAppDispatch } from '@/app/stores/store';
import { toggleSidebar } from '@/app/stores/slices/uiSlice';
import { Button } from '@/components/ui/button'; 
import type { LucideIcon } from 'lucide-react';
import {
  Briefcase,
  CalendarRange,
  ChevronDown,
  Coins,
  DatabaseBackup,
  LayoutDashboard,
  PieChart,
  Receipt,
  RotateCcw,
  Sheet,
  TrendingUp,
  UserCog,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { BrandLogo } from '@/components/brand-logo';

type NavItem = { name: string; path: string; icon: LucideIcon; adminOnly?: boolean };

type NavGroup = { id: string; labelKey: string; icon: LucideIcon; items: NavItem[] };

const navigationGroups: NavGroup[] = [
  {
    id: 'operations',
    labelKey: 'navigation.groupOperations',
    icon: Briefcase,
    items: [
      { name: 'dashboard', path: '/dashboard', icon: LayoutDashboard },
      { name: 'accounts', path: '/accounts', icon: Wallet },
      { name: 'expenses', path: '/expenses', icon: Receipt },
      { name: 'revenues', path: '/revenues', icon: TrendingUp },
      { name: 'currencies', path: '/currencies', icon: Coins },
      { name: 'globalLedgers', path: '/ledgers', icon: CalendarRange },
    ],
  },
  {
    id: 'reports',
    labelKey: 'navigation.groupReports',
    icon: PieChart,
    items: [
      { name: 'reportsAccountSummary', path: '/reports/account-summary', icon: Sheet },
    ],
  },
  // {
  //   id: 'audit',
  //   labelKey: 'navigation.groupAudit',
  //   icon: ShieldCheck,
  //   items: [{ name: 'audit', path: '/audit/wallets', icon: ShieldCheck }],
  // },
  {
    id: 'admin',
    labelKey: 'navigation.groupAdmin',
    icon: UserCog,
    items: [
      { name: 'users', path: '/users', icon: Users, adminOnly: true },
      {
        name: 'backupRestore',
        path: '/admin/backup',
        icon: DatabaseBackup,
        adminOnly: true,
      },
      {
        name: 'rollover',
        path: '/ledgers/rollover',
        icon: RotateCcw,
        adminOnly: true,
      },
    ],
  },
];

function navPathMatches(pathname: string, itemPath: string): boolean {
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

export function Sidebar() {
  const sidebarOpen = useAppSelector((state) => state.ui.sidebarOpen);
  const language = useAppSelector((state) => state.ui.language);
  const theme = useAppSelector((state) => state.ui.theme);
  const role = useAppSelector((state) => state.auth.user?.role);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isRTL = language === 'ar';

  const visibleGroups = useMemo(() => {
    return navigationGroups
      .map((g) => ({
        ...g,
        items: g.items.filter((item) => !item.adminOnly || role === 'ADMIN'),
      }))
      .filter((g) => g.items.length > 0);
  }, [role]);

  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    navigationGroups.forEach((g) => {
      initial[g.id] = true;
    });
    return initial;
  });

  const isNavActive = (itemPath: string) => navPathMatches(pathname, itemPath);

  useEffect(() => {
    setOpenSections((prev) => {
      const next = { ...prev };
      for (const g of visibleGroups) {
        if (g.items.some((item) => navPathMatches(pathname, item.path))) {
          next[g.id] = true;
        }
      }
      return next;
    });
  }, [pathname, visibleGroups]);

  const toggleSection = (id: string) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    // Close sidebar on mobile when navigating
    if (window.innerWidth < 1024) {
      dispatch(toggleSidebar());
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => dispatch(toggleSidebar())}
        />
      )}

      {/* Sidebar */}
      <aside
        dir={isRTL ? 'rtl' : 'ltr'}
        className={cn(
          'fixed top-0 z-50 h-full w-64 border-border/60 bg-card/95 shadow-xl backdrop-blur transition-transform duration-300 lg:translate-x-0',
          isRTL ? 'right-0 border-l' : 'left-0 border-r',
          sidebarOpen 
            ? 'translate-x-0' 
            : isRTL 
              ? 'translate-x-full' 
              : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Sidebar header: logo plate sized to the sidebar width */}
          <div className="flex items-center gap-2 border-b border-border/70 px-3 py-3">
            {isRTL ? (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => dispatch(toggleSidebar())}
                  className="shrink-0 self-start lg:hidden"
                  aria-label={t('common.close')}
                >
                  <X className="h-5 w-5" />
                </Button>
                <button
                  type="button"
                  onClick={() => handleNavigation('/dashboard')}
                  aria-label={`${t('exchange.brand')} — ${t('navigation.dashboard')}`}
                  className="flex min-w-0 flex-1 justify-center rounded-lg transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  <BrandLogo
                    alt=""
                    size="sidebar"
                    className="w-full max-w-full object-contain object-center"
                  />
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => handleNavigation('/dashboard')}
                  aria-label={`${t('exchange.brand')} — ${t('navigation.dashboard')}`}
                  className="flex min-w-0 flex-1 justify-center rounded-lg transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  <BrandLogo
                    alt=""
                    size="sidebar"
                    className="w-full max-w-full object-contain object-center"
                  />
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => dispatch(toggleSidebar())}
                  className="shrink-0 self-start lg:hidden"
                  aria-label={t('common.close')}
                >
                  <X className="h-5 w-5" />
                </Button>
              </>
            )}
          </div>

          {/* Navigation */}
          <nav
            className="flex-1 space-y-3 overflow-y-auto p-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            aria-label={t('navigation.mainNav')}
          >
            {visibleGroups.map((group) => {
              const expanded = openSections[group.id] ?? true;
              const groupActive = group.items.some((item) => isNavActive(item.path));
              const sectionId = `sidebar-section-${group.id}`;

              return (
                <div key={group.id} className="space-y-1">
                  <button
                    type="button"
                    id={`${sectionId}-trigger`}
                    aria-expanded={expanded}
                    aria-controls={sectionId}
                    onClick={() => toggleSection(group.id)}
                    className={cn(
                      'flex w-full items-center justify-between gap-2 rounded-lg px-2 py-2.5 text-start transition-colors',
                      'hover:bg-gray-100 dark:hover:bg-gray-800/80',
                      groupActive
                        ? 'text-primary dark:text-primary'
                        : 'text-gray-700 dark:text-gray-200',
                    )}
                  >
                    <span className="flex min-w-0 flex-1 items-center gap-2.5">
                      <group.icon
                        className={cn(
                          'h-5 w-5 shrink-0',
                          groupActive
                            ? 'text-primary dark:text-primary'
                            : 'text-gray-500 dark:text-gray-400',
                        )}
                        aria-hidden
                      />
                      <span className="truncate text-sm font-semibold leading-snug sm:text-base">
                        {t(group.labelKey)}
                      </span>
                    </span>
                    <ChevronDown
                      className={cn(
                        'h-5 w-5 shrink-0 text-gray-400 transition-transform duration-200 dark:text-gray-500',
                        expanded ? 'rotate-0' : isRTL ? 'rotate-90' : '-rotate-90',
                      )}
                      aria-hidden
                    />
                  </button>
                  <div
                    id={sectionId}
                    role="region"
                    aria-labelledby={`${sectionId}-trigger`}
                    className={cn('space-y-0.5 ps-1', !expanded && 'hidden')}
                  >
                    {group.items.map((item) => (
                      <div
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className={cn(
                          'group flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-start text-sm font-medium text-gray-700 transition-all dark:text-gray-200',
                          isNavActive(item.path) ? 'bg-primary text-white shadow-sm' : '',
                          theme === 'dark' && !isNavActive(item.path)
                            ? 'dark:hover:bg-gray-800/80'
                            : '',
                          theme === 'light' && !isNavActive(item.path)
                            ? 'hover:bg-gray-100 hover:text-gray-900'
                            : '',
                        )}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleNavigation(item.path);
                          }
                        }}
                      >
                        <item.icon
                          className={cn(
                            'h-4 w-4 shrink-0',
                            isNavActive(item.path)
                              ? 'text-white'
                              : 'text-gray-500 group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-200',
                          )}
                          aria-hidden
                        />
                        <span className="min-w-0 flex-1 leading-snug">
                          {t(`navigation.${item.name}`)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </nav>
        </div>
      </aside>
    </>
  );
}